import React, { useState } from "react";
import { toast } from "react-toastify";
import * as finService from "../../services/finService";
import { responseMessage } from "../../services/platformService";
import MasterSection from "../Platform/setup/MasterSection";
import AccessSetup from "../Platform/setup/AccessSetup";
import NumberingSetup from "../Platform/setup/NumberingSetup";
import WorkflowSetup from "../Platform/setup/WorkflowSetup";
import useFinOptions, { optionByCode } from "./useFinOptions";
import { AccountingRules, Ledgers, OpeningBalances } from "./setup/GlSetup";
import CashSetup from "./setup/CashSetup";
import { AssetCategoriesSetup, CoreSourcesSetup } from "./setup/BooksSetup";
import { EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, cardStyle, formatDate, iconBtnStyle, inputStyle, pageStyle, tdStyle, thStyle, useLoad } from "../../components/ui";

// Finance setup (Finance admin): set the company up once - its legal entities, business
// units and locations, currencies and rates, tax, payment terms, how documents are
// numbered, who approves what, and who has access. Everything else in Finance and the
// Dealership builds on it.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const COUNTRIES = [
  { label: "United Arab Emirates", value: "AE" },
  { label: "India", value: "IN" },
];
const LOCATION_TYPES = [
  { label: "Sales / showroom", value: "SALES" },
  { label: "Workshop", value: "WORKSHOP" },
  { label: "Parts", value: "PARTS" },
  { label: "Head office", value: "HQ" },
];
const TAX_TYPES = [
  { label: "Standard rated", value: "STANDARD" },
  { label: "Zero rated", value: "ZERO" },
  { label: "Exempt", value: "EXEMPT" },
  { label: "Reverse charge", value: "REVERSE_CHARGE" },
  { label: "Out of scope", value: "OUT_OF_SCOPE" },
  { label: "Profit margin scheme", value: "MARGIN" },
];
const label = (list, v) => (list.find((o) => o.value === v) || {}).label || v;
const CODE = { key: "code", label: "Code", required: true, max: 16, hint: "Letters, digits, - and _" };
const NAME = { key: "name", label: "Name", required: true, max: 150 };

const masterProps = (key) => ({ load: () => finService.listMaster(key), save: (id, data) => finService.saveMaster(key, id, data) });

/* ------------------------------ organisation ------------------------------ */

function Organisation() {
  const [section, setSection] = useState("le");
  const [changed, setChanged] = useState(0);
  const opts = useFinOptions(["legal-entities", "business-units", "currencies"], changed);
  const currencies = (opts.currencies || []).map((o) => ({ label: o.label, value: o.code }));
  const bump = () => setChanged((n) => n + 1);
  return (
    <>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>Legal entity (own books and tax number) → business unit → location (showroom, workshop, parts counter).</div>
      <Tabs
        active={section}
        onChange={setSection}
        tabs={[
          { key: "le", label: "Legal entities" },
          { key: "bu", label: "Business units" },
          { key: "loc", label: "Locations" },
        ]}
      />
      {section === "le" ? (
        <MasterSection
          {...masterProps("legal-entities")}
          noun="legal entity"
          onChanged={bump}
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Tax number", (r) => r.trn], ["Currency", (r) => r.ledger_currency], ["Country", (r) => label(COUNTRIES, r.country_code)]]}
          fieldKeys={["name", "legal_name", "trn", "country_code", "ledger_currency", "company_segment_value", "address", "emirate_state"]}
          lockedOnEdit={["code"]}
          defaults={{ country_code: "AE", ledger_currency: "AED" }}
          fields={() => [
            CODE,
            NAME,
            { key: "legal_name", label: "Legal name (as on the trade licence)", max: 200 },
            { key: "trn", label: "Tax registration number (TRN / GSTIN)", max: 32 },
            { key: "country_code", label: "Country", type: "select", required: true, options: COUNTRIES },
            { key: "ledger_currency", label: "Ledger currency", type: "select", required: true, options: currencies },
            { key: "company_segment_value", label: "Company segment value (chart of accounts)", max: 25 },
            { key: "address", label: "Address", type: "textarea", max: 500 },
            { key: "emirate_state", label: "Emirate / state", max: 60 },
          ]}
        />
      ) : null}
      {section === "bu" ? (
        <MasterSection
          {...masterProps("business-units")}
          noun="business unit"
          deps={[changed]}
          onChanged={bump}
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Legal entity", (r) => r.legal_entity_name], ["Manager", (r) => r.manager_employee_name]]}
          fieldKeys={["legal_entity_id", "name", "bu_segment_value", "manager_employee_id"]}
          lockedOnEdit={["code"]}
          toForm={(r) => ({ ...r, legal_entity_id: optionByCode(opts["legal-entities"], r.legal_entity_code) })}
          fields={() => [
            { key: "legal_entity_id", label: "Legal entity", type: "select", required: true, options: opts["legal-entities"] || [] },
            CODE,
            NAME,
            { key: "bu_segment_value", label: "Business unit segment value (chart of accounts)", max: 25 },
            { key: "manager_employee_id", label: "Manager (approves as “business unit manager”)", type: "employee" },
          ]}
          emptyText="Add a legal entity first, then its business units."
        />
      ) : null}
      {section === "loc" ? (
        <MasterSection
          {...masterProps("locations")}
          noun="location"
          deps={[changed]}
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Type", (r) => label(LOCATION_TYPES, r.location_type)], ["Business unit", (r) => r.business_unit_name], ["Manager", (r) => r.manager_employee_name]]}
          fieldKeys={["business_unit_id", "name", "location_type", "address", "emirate_state", "default_cost_centre_value", "manager_employee_id"]}
          lockedOnEdit={["code"]}
          defaults={{ location_type: "SALES" }}
          toForm={(r) => ({ ...r, business_unit_id: optionByCode(opts["business-units"], r.business_unit_code) })}
          fields={() => [
            { key: "business_unit_id", label: "Business unit", type: "select", required: true, options: opts["business-units"] || [] },
            { ...CODE, hint: "Shown in document numbers, e.g. QUO-CRA01-30. Cannot be changed later." },
            NAME,
            { key: "location_type", label: "Type", type: "select", required: true, options: LOCATION_TYPES },
            { key: "address", label: "Address", type: "textarea", max: 500 },
            { key: "emirate_state", label: "Emirate / state", max: 60 },
            { key: "default_cost_centre_value", label: "Default cost centre (chart of accounts)", max: 25 },
            { key: "manager_employee_id", label: "Manager (approves as “location manager”)", type: "employee" },
          ]}
        />
      ) : null}
    </>
  );
}

/* ------------------------------ currencies and rates ------------------------------ */

function Rates() {
  const { data, loading, error, reload } = useLoad(() => finService.listRates(), []);
  const opts = useFinOptions(["currencies", "rate-types"]);
  const [adding, setAdding] = useState(false);
  const rows = data || [];
  const codes = (key) => (opts[key] || []).map((o) => ({ label: o.label, value: o.code }));
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setAdding(true)}>Add a rate</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No exchange rates yet" text="1 unit of the first currency = rate units of the second. The reverse direction is worked out automatically." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 520 }}>
              <thead>
                <tr>
                  {["Date", "From", "To", "Type", "Rate"].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>{formatDate(r.rate_date)}</td>
                    <td style={tdStyle}>{r.from_currency}</td>
                    <td style={tdStyle}>{r.to_currency}</td>
                    <td style={tdStyle}>{r.rate_type}</td>
                    <td style={{ ...tdStyle, fontVariantNumeric: "tabular-nums" }}>{Number(r.rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={adding}
        title="Add an exchange rate"
        initial={{ rate_type: "CORPORATE", rate_date: new Date().toISOString().slice(0, 10) }}
        onHide={() => setAdding(false)}
        onSave={(clean) => finService.saveRate({ ...clean, rate: Number(clean.rate) })}
        onDone={reload}
        fields={[
          { key: "from_currency", label: "From currency", type: "select", required: true, options: codes("currencies") },
          { key: "to_currency", label: "To currency", type: "select", required: true, options: codes("currencies") },
          { key: "rate_type", label: "Rate type", type: "select", required: true, options: codes("rate-types") },
          { key: "rate_date", label: "Date", type: "date", required: true },
          { key: "rate", label: "Rate (1 from = rate to)", type: "number", step: "any", min: 0, required: true },
        ]}
      />
    </>
  );
}

function Currencies() {
  const [section, setSection] = useState("ccy");
  return (
    <>
      <Tabs
        active={section}
        onChange={setSection}
        tabs={[
          { key: "ccy", label: "Currencies" },
          { key: "rates", label: "Exchange rates" },
          { key: "types", label: "Rate types" },
        ]}
      />
      {section === "ccy" ? (
        <MasterSection
          {...masterProps("currencies")}
          noun="currency"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Symbol", (r) => r.symbol], ["Decimals", (r) => r.precision_digits]]}
          fieldKeys={["name", "symbol", "precision_digits"]}
          lockedOnEdit={["code"]}
          defaults={{ precision_digits: 2 }}
          fields={() => [
            { key: "code", label: "ISO code", required: true, max: 3 },
            { key: "name", label: "Name", required: true, max: 60 },
            { key: "symbol", label: "Symbol", max: 8 },
            { key: "precision_digits", label: "Decimal places", type: "number", min: 0, required: true },
          ]}
        />
      ) : null}
      {section === "rates" ? <Rates /> : null}
      {section === "types" ? (
        <MasterSection {...masterProps("rate-types")} noun="rate type" columns={[["Code", (r) => r.code], ["Name", (r) => r.name]]} fieldKeys={["name"]} lockedOnEdit={["code"]} fields={() => [CODE, { ...NAME, max: 60 }]} />
      ) : null}
    </>
  );
}

/* ------------------------------ tax ------------------------------ */

function Tax() {
  const [section, setSection] = useState("rates");
  const opts = useFinOptions(["tax-regimes", "legal-entities"]);
  return (
    <>
      <Tabs
        active={section}
        onChange={setSection}
        tabs={[
          { key: "rates", label: "Tax rates" },
          { key: "regs", label: "Registrations" },
          { key: "regimes", label: "Tax regimes" },
        ]}
      />
      {section === "rates" ? (
        <MasterSection
          {...masterProps("tax-rates")}
          noun="tax rate"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Type", (r) => label(TAX_TYPES, r.tax_type)], ["Rate", (r) => `${Number(r.rate_pct)}%`], ["VAT return box", (r) => r.return_box], ["From", (r) => formatDate(r.effective_from)]]}
          fieldKeys={["tax_regime_id", "name", "tax_type", "rate_pct", "recoverable_pct", "return_box", "output_account_key", "input_account_key", "effective_from", "effective_to"]}
          lockedOnEdit={["code"]}
          defaults={{ tax_type: "STANDARD", recoverable_pct: 100 }}
          toForm={(r) => ({ ...r, tax_regime_id: optionByCode(opts["tax-regimes"], r.tax_regime_code) })}
          fields={() => [
            { key: "tax_regime_id", label: "Tax regime", type: "select", required: true, options: opts["tax-regimes"] || [] },
            { ...CODE, hint: "Printed on document lines. Cannot be changed later; add a new code for a new rate." },
            { key: "name", label: "Name", required: true, max: 100 },
            { key: "tax_type", label: "Type", type: "select", required: true, options: TAX_TYPES },
            { key: "rate_pct", label: "Rate %", type: "number", step: "any", min: 0, required: true },
            { key: "recoverable_pct", label: "Recoverable % (input tax)", type: "number", step: "any", min: 0, required: true },
            { key: "return_box", label: "VAT return box", max: 8 },
            { key: "output_account_key", label: "Output tax account key", max: 32 },
            { key: "input_account_key", label: "Input tax account key", max: 32 },
            { key: "effective_from", label: "Effective from", type: "date", required: true },
            { key: "effective_to", label: "Effective to", type: "date" },
          ]}
        />
      ) : null}
      {section === "regs" ? (
        <MasterSection
          {...masterProps("tax-registrations")}
          noun="tax registration"
          columns={[["Number", (r) => r.registration_number], ["Legal entity", (r) => r.legal_entity_name], ["Regime", (r) => r.tax_regime_name], ["Emirate / state", (r) => r.emirate_state], ["From", (r) => formatDate(r.effective_from)]]}
          fieldKeys={["legal_entity_id", "tax_regime_id", "registration_number", "emirate_state", "effective_from"]}
          toForm={(r) => ({ ...r, legal_entity_id: optionByCode(opts["legal-entities"], r.legal_entity_code), tax_regime_id: optionByCode(opts["tax-regimes"], r.tax_regime_code) })}
          fields={() => [
            { key: "legal_entity_id", label: "Legal entity", type: "select", required: true, options: opts["legal-entities"] || [] },
            { key: "tax_regime_id", label: "Tax regime", type: "select", required: true, options: opts["tax-regimes"] || [] },
            { key: "registration_number", label: "Registration number", required: true, max: 32 },
            { key: "emirate_state", label: "Emirate / state", max: 60 },
            { key: "effective_from", label: "Registered from", type: "date", required: true },
          ]}
        />
      ) : null}
      {section === "regimes" ? (
        <MasterSection
          {...masterProps("tax-regimes")}
          noun="tax regime"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Country", (r) => label(COUNTRIES, r.country_code)]]}
          fieldKeys={["name", "country_code"]}
          lockedOnEdit={["code"]}
          fields={() => [CODE, { ...NAME, max: 100 }, { key: "country_code", label: "Country", type: "select", required: true, options: COUNTRIES }]}
        />
      ) : null}
    </>
  );
}

/* ------------------------------ payment terms ------------------------------ */

function TermLines({ term, onHide }) {
  const { data, loading, error, reload } = useLoad(() => finService.getTermLines(term.id), [term.id]);
  const [lines, setLines] = useState(null);
  const [saving, setSaving] = useState(false);
  const rows = lines || (data || []).map((l) => ({ due_days: l.due_days, percent: Number(l.percent) }));
  const total = Math.round(rows.reduce((s, l) => s + (Number(l.percent) || 0) * 10000, 0)) / 10000;
  const setRow = (i, patch) => setLines(rows.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    setSaving(true);
    try {
      const res = await finService.saveTermLines(term.id, rows.map((l) => ({ due_days: Number(l.due_days), percent: Number(l.percent) })));
      if (res?.success) {
        toast.success(res.message || "Saved");
        onHide();
      } else toast.error(responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show
      onHide={() => !saving && onHide()}
      title={`Instalments – ${term.name}`}
      width={520}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || total !== 100}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.map((l, i) => (
          <div key={i} className="d-flex gap-2 align-items-end">
            <Field label={i === 0 ? "Due after (days)" : null} style={{ flex: 1 }}>
              <input type="number" min={0} className="form-control" style={inputStyle} value={l.due_days} onChange={(e) => setRow(i, { due_days: e.target.value })} aria-label={`Instalment ${i + 1} due days`} />
            </Field>
            <Field label={i === 0 ? "Percent" : null} style={{ flex: 1 }}>
              <input type="number" min={0} step="any" className="form-control" style={inputStyle} value={l.percent} onChange={(e) => setRow(i, { percent: e.target.value })} aria-label={`Instalment ${i + 1} percent`} />
            </Field>
            {rows.length > 1 ? (
              <button type="button" className="mb-3" style={linkBtn} onClick={() => setLines(rows.filter((_, j) => j !== i))}>
                Remove
              </button>
            ) : null}
          </div>
        ))}
        <div className="d-flex justify-content-between align-items-center">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines([...rows, { due_days: rows.length ? Number(rows[rows.length - 1].due_days) + 30 : 0, percent: Math.max(0, 100 - total) }])}>
            Add instalment
          </button>
          <span style={{ fontSize: 13, fontWeight: 600, color: total === 100 ? "#065F46" : "#991B1B" }}>Total {total}%</span>
        </div>
      </LoadState>
    </SlidePanel>
  );
}

function PaymentTerms() {
  const [openTerm, setOpenTerm] = useState(null);
  return (
    <>
      <MasterSection
        {...masterProps("payment-terms")}
        noun="payment term"
        columns={[["Code", (r) => r.code], ["Name", (r) => r.name]]}
        fieldKeys={["name"]}
        lockedOnEdit={["code"]}
        fields={() => [CODE, { ...NAME, max: 100 }]}
        emptyText="A new term is due in full on the invoice date until you set its instalments."
        extraActions={(r) => (
          <>
            <button type="button" style={linkBtn} onClick={() => setOpenTerm(r)}>
              Instalments
            </button>{" "}
          </>
        )}
      />
      {openTerm ? <TermLines term={openTerm} onHide={() => setOpenTerm(null)} /> : null}
    </>
  );
}

/* ------------------------------ general ------------------------------ */

function General() {
  const { data, loading, error, reload } = useLoad(() => finService.getSettings(), []);
  const opts = useFinOptions(["legal-entities", "rate-types"]);
  const [editing, setEditing] = useState(false);
  const s = data || {};
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...cardStyle, padding: 20, maxWidth: 640 }}>
        <div className="d-flex justify-content-between mb-3">
          <h6 className="mb-0">General</h6>
          <button type="button" style={linkBtn} onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
        <div style={{ fontSize: 14, lineHeight: 2 }}>
          <div>Country pack: {label(COUNTRIES, s.country_pack)}</div>
          <div>Default legal entity: {s.default_legal_entity_code ? `${s.default_legal_entity_code} – ${s.default_legal_entity_name}` : "—"}</div>
          <div>Default rate type: {s.default_rate_type || "—"}</div>
        </div>
      </div>
      <FormDrawer
        show={editing}
        title="General"
        initial={{ country_pack: s.country_pack, default_rate_type: s.default_rate_type, default_legal_entity_id: optionByCode(opts["legal-entities"], s.default_legal_entity_code) }}
        onHide={() => setEditing(false)}
        onSave={(clean) => finService.saveSettings(clean)}
        onDone={reload}
        fields={[
          { key: "country_pack", label: "Country pack (tax rules)", type: "select", options: COUNTRIES },
          { key: "default_legal_entity_id", label: "Default legal entity", type: "select", options: opts["legal-entities"] || [] },
          { key: "default_rate_type", label: "Default exchange rate type", type: "select", options: (opts["rate-types"] || []).map((o) => ({ label: o.label, value: o.code })) },
        ]}
      />
    </LoadState>
  );
}

const TABS = [
  { key: "org", label: "Organisation", render: () => <Organisation /> },
  { key: "currencies", label: "Currencies & rates", render: () => <Currencies /> },
  { key: "tax", label: "Tax", render: () => <Tax /> },
  { key: "terms", label: "Payment terms", render: () => <PaymentTerms /> },
  { key: "ledgers", label: "Ledgers", render: () => <Ledgers /> },
  { key: "cash", label: "Cash & bank", render: () => <CashSetup /> },
  { key: "assets", label: "Fixed assets", render: () => <AssetCategoriesSetup /> },
  { key: "sources", label: "Core sources", render: () => <CoreSourcesSetup /> },
  { key: "rules", label: "Accounting rules", render: () => <AccountingRules /> },
  { key: "opening", label: "Opening balances", render: () => <OpeningBalances /> },
  { key: "numbering", label: "Document numbering", render: () => <NumberingSetup module="fin" /> },
  { key: "approvals", label: "Approvals", render: () => <WorkflowSetup module="fin" /> },
  { key: "access", label: "Access", render: () => <AccessSetup module="fin" label="Finance" /> },
  { key: "general", label: "General", render: () => <General /> },
];

export default function FinanceSetup() {
  const [tab, setTab] = useState("org");
  const active = TABS.find((t) => t.key === tab);
  return (
    <div style={pageStyle}>
      <PageHeader title="Finance Setup" subtitle="Set your company up once; Finance and the Dealership build on it" />
      <Tabs tabs={TABS.map(({ key, label: l }) => ({ key, label: l }))} active={tab} onChange={setTab} />
      {active.render()}
    </div>
  );
}
